import { SolidCloseCircle } from '@qr-front/icons/solid-close-circle'
import { subscriptionAtom } from 'features/shared/client'
import { useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'

import { userAtom } from '@/global-state'

import { useFormatDate } from '../../../../../hooks/dateFormattingHooks'
import { UserStatusEnum } from '../../../../../types/subscription'
import { TTrialCountdown } from './TrialCountdown'

const getRestrictedFeatures = (areRestrictionsActive: boolean) => [
	{
		feature: 'scan',
		translationKey: areRestrictionsActive
			? 'dashboard__trial_expired__modal__scan_feature_restricted__active'
			: 'dashboard__trial_expired__modal__scan_feature_restricted',
	},
	{
		feature: 'create_qr_code',
		translationKey: areRestrictionsActive
			? 'dashboard__trial_expired__modal__create_qr_code_feature_restricted__active'
			: 'dashboard__trial_expired__modal__create_qr_code_feature_restricted',
	},
	{
		feature: 'analytics',
		translationKey: areRestrictionsActive
			? 'dashboard__trial_expired__modal__analytics_feature_restricted__active'
			: 'dashboard__trial_expired__modal__analytics_feature_restricted',
	},
	{
		feature: 'download',
		translationKey: areRestrictionsActive
			? 'dashboard__trial_expired__modal__download_feature_restricted__active'
			: 'dashboard__trial_expired__modal__download_feature_restricted',
	},
]

export const SubExpiredRestrictedFeatures = ({
	trialCountDown,
}: {
	trialCountDown: TTrialCountdown | null
}) => {
	const t = useTranslations()

	const subscription = useAtomValue(subscriptionAtom)

	const formatDate = useFormatDate()

	const trialExpireDate = subscription?.expiresAt
		? formatDate(subscription.expiresAt)
		: trialCountDown?.expiresAt
			? formatDate(trialCountDown.expiresAt)
			: null

	const user = useAtomValue(userAtom)

	const isFreemiumOldSub = user?.status === UserStatusEnum.FREEMIUM_OLD
	const isPremiumCanceledSub = user?.status === UserStatusEnum.PREMIUM_CANCELLED
	const isPremiumInDebtSub = user?.status === UserStatusEnum.PREMIUM_IN_DEBT

	const getTitleTKey = () => {
		if (isFreemiumOldSub || isPremiumCanceledSub || isPremiumInDebtSub) {
			return 'dashboard__trial_expired__modal__expired_date__premium_canceled'
		}
		const isTrialExpired = trialCountDown?.expired
		return isTrialExpired
			? 'dashboard__trial_expired__modal__expired_date'
			: 'dashboard__trial_expired__modal__expire_date'
	}

	const areRestrictionsActive =
		isFreemiumOldSub || isPremiumCanceledSub || isPremiumInDebtSub

	return (
		<div className="flex flex-col gap-6 rounded-8 bg-generator-bg p-4">
			{trialExpireDate && (
				<h3 className="type-body-medium">
					{t.rich(getTitleTKey(), {
						expires_at: () => (
							<span className="font-semibold">{trialExpireDate}</span>
						),
					})}
				</h3>
			)}
			<div className="flex w-full flex-col gap-2">
				{getRestrictedFeatures(areRestrictionsActive).map(
					({ feature, translationKey }) => (
						<div
							className="flex items-center justify-start gap-2"
							key={feature}
						>
							<SolidCloseCircle className="size-6 shrink-0 text-error" />
							<p className="type-body-small word-break-fix">
								{t(translationKey)}
							</p>
						</div>
					),
				)}
			</div>
		</div>
	)
}
